import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router';

import { Box } from '@mui/material';
import ErrorHandlingLayout from '@sharedCR/common/ErrorHandlingLayout';
import { EditPenIcon } from '@sharedCR/coreDesignSystem/icons';
import MenuPopoverCDS from '@sharedCR/coreDesignSystem/MenuPopoverCDS';
import { LoansPaths } from 'app/configs/routesConfig/loans';
import Skeleton from 'app/shared/components/common/Skeleton';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import { compose } from 'redux';

import { IconSize } from 'constants/coreDesignSystem';
import { AttendantCompanyTypeEnum } from 'constants/loanApplication';
import { PAGE_CODES } from 'constants/pageCodes';
import { SKELETON_TYPES } from 'constants/skeleton';
import useCompanyId from 'hooks/useCompanyId';
import useLoanId from 'hooks/useLoanId';
import usePendingChangeAuthors from 'hooks/usePendingChangeAuthors';
import usePendingChangesDependentFetch from 'hooks/usePendingChangesDependentFetch';
import usePopover from 'hooks/usePopover';
import { getConditionalArray } from 'utils/data';

import { selectWarehouseBankOptions } from 'store/accounting/selectors';
import { getMasterPermissionsToPage } from 'store/auth/selectors';
import { getBusinessDaysAction } from 'store/companies/actions';
import {
  getApplicationBorrowersAction,
  getAttendantCompaniesAction,
  getClosingDocumentsAction,
  getClosingInfoAction,
  getLoanTitleHoldersAction,
  getPropertiesActionV2,
  getPropertyAndLoanSummaryAction,
  getTrustInfoAction,
  getWireInstructionConfirmationAction,
} from 'store/loanApplications/actions';
import {
  selectClosingCompanies,
  selectClosingDocuments,
  selectClosingInfo,
  selectDealFromApplication,
  selectEntityChangedPropsById,
  selectFundingInfo,
  selectIsLoanApplicationEditAllowed,
  selectIsLoanCanceled,
  selectIsLoanClosingTwoPart,
  selectIsUnsecuredFromPropertyAndLoanSummary,
  selectLoanClosingChangedPropsById,
  selectScheduleClosingTask,
} from 'store/loanApplications/selectors';

import ClosingDocumentsSection from './components/ClosingDocumentsSection';
import ClosingInformationSection from './components/ClosingInformationSection';
import FundingInformationSection from './components/FundingInformationSection';
import ScheduleClosingSection from './components/ScheduleClosingSection';
import SnapdocsClosingSection from './components/SnapdocsClosingSection';
import { EditDrawer } from './EditDrawer';
import { SECTION_HREF_MAP } from './EditDrawer/EditDrawer.config';
import { wrapperStyles } from './LoanClosingCDS.styles';
import { LoanClosingCDSTestIds } from './LoanClosingCDS.testIds';

const LoanClosingCDS = ({ isLoading, setIsLoading }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { search } = useLocation();
  const { applicationId, tab } = useParams();

  const [isError, setIsError] = useState(false);
  const isAmortizationModalOpenRef = useRef(false);

  const loanId = useLoanId();
  const companyId = useCompanyId();

  const closingInfo = useSelector(selectClosingInfo);
  const closingCompanies = useSelector(selectClosingCompanies);
  const closingDocuments = useSelector(selectClosingDocuments);
  const fundingInfo = useSelector(selectFundingInfo);
  const isTwoPartClosing = useSelector(selectIsLoanClosingTwoPart);
  const isUnsecured = useSelector(selectIsUnsecuredFromPropertyAndLoanSummary);
  const warehouseBankOptions = useSelector(selectWarehouseBankOptions);
  const scheduleClosingTask = useSelector(selectScheduleClosingTask);
  const { loanId: dealLoanId } = useSelector(selectDealFromApplication);
  const pendingChangesData = useSelector(
    selectLoanClosingChangedPropsById({
      loanClosingId: closingInfo?.loanClosingId,
      loanId: dealLoanId,
    }),
  );

  const titleCompany = useMemo(
    () => closingCompanies?.find(c => c.closingCompanyType === AttendantCompanyTypeEnum.TITLE),
    [closingCompanies],
  );
  const titleCompanyPendingChanges = useSelector(
    selectEntityChangedPropsById(`${closingInfo?.loanClosingId}_${AttendantCompanyTypeEnum.TITLE}`),
  );
  const titleLocationPendingChanges = useSelector(
    selectEntityChangedPropsById(titleCompany?.closingCompanyLocationId),
  );
  const fieldAuthors = usePendingChangeAuthors(closingInfo?.loanClosingId, dealLoanId);
  const titleCompanyAuthors = usePendingChangeAuthors(
    closingInfo?.loanClosingId && `${closingInfo.loanClosingId}_${AttendantCompanyTypeEnum.TITLE}`,
  );
  const titleLocationAuthors = usePendingChangeAuthors(titleCompany?.closingCompanyLocationId);

  const fetchData = useCallback(() => {
    setIsError(false);
    setIsLoading(true);

    Promise.all([
      ...getConditionalArray(loanId, () => [
        dispatch(getClosingDocumentsAction({ loanId, companyId })),
        dispatch(getClosingInfoAction({ loanId })),
        dispatch(getWireInstructionConfirmationAction({ loanId })),
      ]),
      dispatch(getPropertyAndLoanSummaryAction({ applicationId })),
      // The closing date cascade derives the rescission date from the subject property's
      // occupancy and its interest-credit default from the property state, both of which
      // live in this slice. Without it the cascade silently produced nothing (DEV-6663).
      dispatch(getPropertiesActionV2({ applicationId, withSubjectProperty: true })),
      dispatch(getBusinessDaysAction({ companyId })),
      dispatch(getApplicationBorrowersAction({ applicationId })),
      ...getConditionalArray(!isUnsecured, () => [
        dispatch(getAttendantCompaniesAction()),
        dispatch(getLoanTitleHoldersAction({ applicationId })),
        dispatch(getTrustInfoAction({ applicationId })),
      ]),
    ])
      .catch(() => setIsError(true))
      .finally(() => setIsLoading(false));
  }, [dispatch, applicationId, loanId, companyId, isUnsecured, setIsLoading]);

  const isEditAllowed = useSelector(selectIsLoanApplicationEditAllowed);
  const { isEditAllowed: isClosingEditAllowed } = useSelector(
    getMasterPermissionsToPage(PAGE_CODES.LOAN_EDITOR_CLOSING),
  );
  const isLoanCanceled = useSelector(selectIsLoanCanceled);
  const isActionsVisible = isEditAllowed && isClosingEditAllowed && !isLoanCanceled;

  const closingInfoPopover = usePopover();
  const fundingInfoPopover = usePopover();

  usePendingChangesDependentFetch(fetchData);

  const handleEditClick = useCallback(
    (sectionHref, onClearAnchorElement) => () => {
      onClearAnchorElement();
      const path = LoansPaths.applicationManagement.generatePath({
        applicationId,
        tab,
        subtab: sectionHref,
      });
      history.push({ pathname: path, search });
    },
    [applicationId, tab, history, search],
  );

  const getClosingInfoMenuItems = useCallback(
    onClearAnchorElement => [
      {
        text: 'Edit Closing Information',
        onClick: handleEditClick(SECTION_HREF_MAP.SCHEDULING, onClearAnchorElement),
        icon: <EditPenIcon size={IconSize[20]} />,
        disabled: !isActionsVisible,
      },
    ],
    [handleEditClick, isActionsVisible],
  );

  const getFundingInfoMenuItems = useCallback(
    onClearAnchorElement => [
      {
        text: 'Edit Funding Information',
        onClick: handleEditClick(SECTION_HREF_MAP.FUNDING_INFORMATION, onClearAnchorElement),
        icon: <EditPenIcon size={IconSize[20]} />,
        disabled: !isActionsVisible,
      },
    ],
    [handleEditClick, isActionsVisible],
  );

  return (
    <>
      {isLoading ? (
        <Skeleton length={3} type={SKELETON_TYPES.CARD} />
      ) : (
        <ErrorHandlingLayout isError={isError} onClick={fetchData}>
          <Box data-testid={LoanClosingCDSTestIds.LOAN_CLOSING_CDS} sx={wrapperStyles}>
            <ScheduleClosingSection task={scheduleClosingTask} />
            <SnapdocsClosingSection applicationId={applicationId} companyId={companyId} />
            <ClosingInformationSection
              applicationId={applicationId}
              closingCompanies={closingCompanies}
              closingInfo={closingInfo}
              companyAuthors={titleCompanyAuthors}
              companyPendingChanges={titleCompanyPendingChanges}
              fieldAuthors={fieldAuthors}
              isEditAllowed={isActionsVisible}
              isMenuOpened={!!closingInfoPopover.anchorEl}
              isTwoPartClosing={isTwoPartClosing}
              isUnsecured={isUnsecured}
              locationAuthors={titleLocationAuthors}
              locationPendingChanges={titleLocationPendingChanges}
              pendingChangesData={pendingChangesData}
              onMenuClick={closingInfoPopover.onSetAnchorElement}
            />
            <MenuPopoverCDS
              anchorEl={closingInfoPopover.anchorEl}
              items={getClosingInfoMenuItems(closingInfoPopover.onClearAnchorElement)}
              onClose={closingInfoPopover.onClearAnchorElement}
            />
            <FundingInformationSection
              fieldAuthors={fieldAuthors}
              fundingInfo={fundingInfo}
              isEditAllowed={isActionsVisible}
              isMenuOpened={!!fundingInfoPopover.anchorEl}
              isUnsecured={isUnsecured}
              pendingChangesData={pendingChangesData}
              warehouseBankOptions={warehouseBankOptions}
              onMenuClick={fundingInfoPopover.onSetAnchorElement}
            />
            <MenuPopoverCDS
              anchorEl={fundingInfoPopover.anchorEl}
              items={getFundingInfoMenuItems(fundingInfoPopover.onClearAnchorElement)}
              onClose={fundingInfoPopover.onClearAnchorElement}
            />
            <ClosingDocumentsSection
              closingDocuments={closingDocuments}
              isModalOpenRef={isAmortizationModalOpenRef}
            />
          </Box>
        </ErrorHandlingLayout>
      )}

      <EditDrawer />
    </>
  );
};

LoanClosingCDS.propTypes = {
  ...WithLoadingPropTypes,
};

export default compose(withLoading, memo)(LoanClosingCDS);
